/**
 * MagnetParser — parse UNTRUSTED USER INPUT magnet URIs into structured data.
 *
 * ── SCOPE ────────────────────────────────────────────────────────────────────
 * ONLY for user-provided input: pasted links, QR scans, deep link resolution.
 * NEVER called on engine output (use handle.torrentIdentity directly).
 *
 * ── DOES NOT ─────────────────────────────────────────────────────────────────
 * - Implement any BitTorrent protocol
 * - Reconstruct or modify URIs (originalUri preserved verbatim)
 *
 * ── USES ─────────────────────────────────────────────────────────────────────
 * - Platform URLSearchParams for all query string parsing and decoding.
 *   URLSearchParams already URL-decodes values — do NOT call decodeURIComponent.
 *
 * ── SUPPORTED FORMATS ────────────────────────────────────────────────────────
 * v1:     magnet:?xt=urn:btih:<40-char hex SHA-1>
 * v2:     magnet:?xt=urn:btmh:1220<64-char hex SHA-256>  (future)
 * hybrid: both xt= params in the same URI                (future)
 *
 * Base32 infoHash (32-char): WebTorrent normalizes these internally when the
 * engine resolves the magnet. parseMagnet accepts them but does NOT normalize
 * them itself — if you need a canonical v1 hex string, pass through the engine.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { TorrentIdentity } from '@share/domain';
import { SharePayloadSchema } from '../../shared/validation/protocol.schema';

export interface ParsedMagnet {
  /** Preserved verbatim — never reconstructed or modified. */
  originalUri: string;
  /** Content identity extracted from xt= parameters. */
  identity: TorrentIdentity;
  /**
   * Display name from dn= — URLSearchParams already URL-decodes this.
   * DO NOT call decodeURIComponent on this value.
   */
  displayName?: string;
  /** All tracker URLs from tr= parameters, in declaration order. */
  trackers: string[];
}

/** Thrown when a string cannot be parsed as a valid Magnet URI. */
export class MagnetParseError extends Error {
  constructor(message: string, public readonly uri: string) {
    super(message);
    this.name = 'MagnetParseError';
  }
}

/**
 * Parse a user-provided Magnet URI into a structured ParsedMagnet.
 *
 * @param uri - A Magnet URI string starting with "magnet:?"
 * @throws {MagnetParseError} if the URI is not a valid magnet or contains no
 *   recognizable torrent identity. Never returns null.
 */
export function parseMagnet(uri: string): ParsedMagnet {
  if (!uri.startsWith('magnet:?')) {
    throw new MagnetParseError(
      'Not a magnet URI: must start with "magnet:?"',
      uri
    );
  }

  // URLSearchParams handles all encoding, decoding, and multiple same-key values
  const params = new URLSearchParams(uri.slice('magnet:?'.length));
  const xtValues = params.getAll('xt');

  if (xtValues.length === 0) {
    throw new MagnetParseError(
      'Magnet URI has no xt (exact topic) parameter',
      uri
    );
  }

  const identity: TorrentIdentity = {};

  for (const xt of xtValues) {
    // v1: urn:btih:<SHA-1>
    // Accepts 40-char hex OR 32-char base32 (WebTorrent resolves either when used with engine)
    if (xt.startsWith('urn:btih:')) {
      const raw = xt.slice('urn:btih:'.length).toLowerCase();
      
      if (raw.length === 32 || SharePayloadSchema.shape.infoHash.safeParse(raw).success) {
        // Base32 infoHash (32) or valid hex hash (40/64)
        identity.v1 = raw;
      } else {
        throw new MagnetParseError(
          `Invalid btih hash length: expected 32 (base32) or 40/64 (hex chars), got ${raw.length}`,
          uri
        );
      }
    }
    // v2: urn:btmh:1220<SHA-256-hex>
    // "1220" = multihash prefix (0x12 = sha2-256 algo, 0x20 = 32 bytes length)
    if (xt.startsWith('urn:btmh:')) {
      const multihash = xt.slice('urn:btmh:'.length).toLowerCase();
      if (!multihash.startsWith('1220') || multihash.length !== 68) {
        throw new MagnetParseError(
          `Invalid btmh value: expected "1220" + 64-char hex (68 chars total), got "${multihash}"`,
          uri
        );
      }
      identity.v2 = multihash.slice(4); // strip "1220" prefix → pure 64-char SHA-256 hex
    }
  }

  if (!identity.v1 && !identity.v2) {
    throw new MagnetParseError(
      'Magnet URI contains no supported torrent identity (urn:btih or urn:btmh)',
      uri
    );
  }

  // URLSearchParams.get() already URL-decodes — do NOT call decodeURIComponent again
  const displayName = params.get('dn') ?? undefined;
  const trackers = params.getAll('tr');

  return {
    originalUri: uri,
    identity,
    displayName,
    trackers,
  };
}
